<script lang="ts">
export interface MenuItemProps {
  /**
   * Whether the menu item is selected.
   * @defaultValue false
   */
  selected?: boolean;

  /**
   * The label of the MenuItem
   */
  label?: string;

  /**
   * The description of the MenuItem
   */
  description?: string;

  /**
   * The icon that will be displayed in the leading slot.
   */
  icon?: string;
  /**
   * The icon that will be displayed in the trailing slot.
   */
  trailingIcon?: string;
  /**
   * The external icon that is shown when target is `_blank`.
   * @defaultValue e-menu's externalIcon
   */
  externalIcon?: string;
  /**
   * The type of the item.
   * The `label` type is only displayed in `vertical` orientation.
   * The `trigger` type is used to force the item to be collapible when its a link in `vertical` orientation.
   * @defaultValue 'link'
   */
  type?: 'label' | 'trigger' | 'link';
  /**
   * Where to display the linked URL, as the name for a browsing context.
   */
  target?: '_blank' | '_parent' | '_self' | '_top' | (string & {}) | null;
  /**
   * The URL that the menu item should navigate to.
   */
  to?: string;
  /**
   * The children of the item.
   */
  children?: MenuItemProps[];
  /**
   * If this item should be open by default if it has children.
   */
  opened?: boolean;
  /**
   * Whether the menu is in collapsed state. Passed down from parent menu.
   */
  collapsed?: boolean;
  /**
   * If 'true', the item will be disabled.
   */
  disabled?: boolean;

  /**
   * Additional classes to apply to the menu item.
   */
  cssClass?: string;
}

export interface MenuItemSlots extends Record<string, unknown> {
  /**
   * Contains the leading icon by default.
   */
  leading?: unknown;
  /**
   * Contains the label.
   */
  default?: unknown;
  /**
   * Contains the trailing icon by default.
   * When it has children, trailingIcon will be `chevron-right`.
   * When target is `_blank`, trailingIcon will be `open-in-new`.
   */
  trailing?: unknown;
  /**
   * Contains the child items by default.
   * When children has length or this slot contains content, The MenuItem will be collapsible.
   */
  children?: unknown;
}
</script>

<script lang="ts" setup generic="T extends MenuItemProps">
import { useSharedState } from '@/runtime/composables/useSharedState';
import { computed, useHost } from 'vue';
import { useComponentContext } from '@/runtime/composables/useComponentContext';
import { useLayout } from 'components/Layout/useLayout';
import { usePersistentRef } from '@/runtime/composables/usePersistentRef';

defineOptions({ name: 'MenuItem' });
const props = withDefaults(defineProps<MenuItemProps>(), {
  selected: false,
  opened: false,
  type: 'link',
  children: () => []
});

const emit = defineEmits<(event: 'update:opened', value: boolean) => void>();

defineSlots<MenuItemSlots>();

const { toggleCollapsed, collapsed: layoutCollapsed } = useLayout();
const { parentState, childrenStates } = useComponentContext<MenuItemSlots>();
const host = useHost();
const state = useSharedState(props);

const isSubItem = computed(() => parentState.value?.type === 'MenuItem');

// Persist opened state with position-based scoping
if (state.opened && host) {
  usePersistentRef(state.opened, {
    key: 'e-menu-item:opened',
    scope: 'position',
    options: {
      host,
      props: {
        label: props.label,
        to: props.to,
        icon: props.icon
      }
    }
  });
}

const registeredChildren = computed((): MenuItemProps[] => {
  return childrenStates.value
    .map(c => c.props as MenuItemProps)
    .filter(childProps => childProps != null);
});

const hasChildren = computed(() => registeredChildren.value.length > 0);

const openedProxy = computed({
  get: () => props.opened ?? false,
  set: (v: boolean) => {
    emit('update:opened', v);
    if (v && layoutCollapsed.value) {
      toggleCollapsed();
    }
  }
});

const isCollapsed = computed(() => state.collapsed?.value || layoutCollapsed?.value);
</script>

<template>
  <li :class="[type === 'label' && 'e-menu-item-label']">
    <a
      v-if="!hasChildren"
      :class="[type !== 'label' && 'e-menu-item', isSubItem && 'e-submenu-item', cssClass || null]"
      :href="to"
      :target="target ?? undefined"
      :aria-disabled="disabled"
      :selected="selected ? '' : null"
    >
      <div class="e-menu-icon">
        <slot name="leading">
          <e-icon
            v-if="type !== 'label' && icon"
            :name="icon ?? (isSubItem ? '' : 'circle')"
          ></e-icon>
        </slot>
      </div>

      <div
        v-show="!isCollapsed"
        class="contents"
      >
        <slot>
          <span class="e-menu-item-title">{{ state.label?.value }}</span>
        </slot>
      </div>

      <slot name="trailing"> </slot>
    </a>

    <details
      v-else-if="hasChildren"
      class="e-menu-item-details"
      :class="disabled && 'e-menu-disabled'"
      :open="openedProxy && !isCollapsed"
      @toggle="openedProxy = ($event.target as HTMLDetailsElement).open"
    >
      <summary class="e-menu-item-details-summary">
        <a
          :href="to"
          class="e-menu-item"
          :class="[isSubItem && 'e-submenu-item']"
          :selected="selected ? '' : null"
        >
          <div class="e-menu-icon">
            <slot name="leading">
              <e-icon
                v-if="icon"
                :name="icon ?? (isSubItem ? null : 'circle')"
              ></e-icon>
            </slot>
          </div>

          <div
            v-show="!isCollapsed"
            class="contents"
          >
            <slot>
              <span class="e-menu-item-title">{{ state.label?.value }}</span>
            </slot>
          </div>

          <slot name="trailing">
            <e-icon
              v-if="hasChildren && !isCollapsed"
              class="e-menu-icon e-menu-item-details-trailing-icon"
              :name="target === '_blank' ? 'open-in-new' : 'chevron-right'"
            ></e-icon>
          </slot>
        </a>
      </summary>
      <ul
        v-show="hasChildren && !isCollapsed"
        class="e-menu-item-details-content"
      >
        <slot name="children">
          <e-menu-item
            v-for="(child, index) in children"
            :key="index"
            v-bind="child"
          />
        </slot>
      </ul>
    </details>
  </li>
</template>
